import { getAdminToken, type ValidateResponse } from '@/lib/auth'

const API_BASE_URL = '/api'

export interface Device {
  id: number
  site_no: number
  name: string | null
  modem: number | null
  location: string | null
  rego: string | null
  bt_device: number | null
  no_of_sightings: number
  sightings_sparkline: string | null
  when: string | null
  last_detection: string | null
}

export interface TravelTimeLink {
  linkName: string
  subtitle?: string
  baseTT: number
  baseSpeed: number
  delay: number
  currentTT: number
  currentSpeed: number
  last90MinTT: string
  last90MinBase: string
}

export interface TravelTimeLinkMeta {
  link_name: string
  subtitle?: string | null
}

export interface GraphDataResponse {
  data: string
  links?: TravelTimeLinkMeta[]
}

export interface ReportLinkData {
  link_name: string
  data: Array<{
    time?: string
    day?: string
    baseline: number
    actual: number
  }>
}

export interface ReportData {
  type: string
  links: ReportLinkData[]
}

// --- Admin types ---

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  page_size: number
}

export interface AdminDevice {
  id: number
  serial_no: string | null
  name: string | null
  owner: string | null
  data_user: string | null
  project: string | null
  current_location: string | null
  connected: boolean | null
  timeof: string | null
  gps_lat: number | null
  gps_long: number | null
  host_address: string | null
  device_port: number | null
  modem_port: number | null
  /** Own registration if the sensor rides its own trailer, else the attached host's. */
  rego: string | null
  /** True when `rego` came from the attached host rather than from the sensor itself. */
  rego_is_inherited: boolean
}

export interface AdminLink {
  /**
   * The one id a link has, everywhere: Omnibus, this admin UI, and data-api (published to
   * vendors as "J1" + id). The legacy 109-169 numbering was retired on 2026-07-30 —
   * `travel_time_segments.legacy_bt_link_id` survives as provenance only.
   */
  id: number
  text: string | null
  from_device_id: number | null
  to_device_id: number | null
  travel_time: number | null
  avg_travel_time: number | null
  base_travel_time: number | null
  link_length: number | null
  speed_limit: number | null
  min_travel_time: number | null
  allowed_variance: number | null
  time_arrived: number | null
  project: string | null
  default_travel_time: number | null
}

export interface AdminTravelTime {
  link_id: number
  mac_id: number
  finish_timestamp: number
  duration_seconds: number | null
  average_duration: number | null
  base_duration: number | null
}

export interface AdminSighting {
  device_id: number
  sighting_mac_address: number
  sighting_depart: number
  sighting_arrive: number | null
}

export interface AdminBaseTT {
  link_id: number
  base_tt: string | null
}

export interface AdminBaseData {
  base_id: number
  link_no: number
  interval: number
  seconds: number | null
}

async function request(endpoint: string, options: RequestInit = {}) {
  const url = `${API_BASE_URL}${endpoint}`
  const headers = new Headers(options.headers)

  if (options.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }

  const adminToken = getAdminToken()
  if (adminToken) {
    headers.set('Authorization', `Bearer ${adminToken}`)
  }

  const config: RequestInit = {
    headers,
    ...options,
  }

  const response = await fetch(url, config)

  if (!response.ok) {
    const contentType = response.headers.get('content-type') || ''
    let message = `HTTP error! status: ${response.status}`

    if (contentType.includes('application/json')) {
      const payload = await response.json().catch(() => null)
      if (payload && typeof payload.detail === 'string') {
        message = payload.detail
      }
    } else {
      const text = await response.text().catch(() => '')
      if (text) {
        message = text
      }
    }

    throw new Error(message)
  }

  const contentType = response.headers.get('content-type')
  if (contentType && contentType.includes('application/json')) {
    return response.json()
  }

  if (contentType && contentType.includes('application/vnd.openxmlformats')) {
    return response
  }

  return response.text()
}

async function get(endpoint: string, params: Record<string, string> = {}) {
  const queryString = new URLSearchParams(params).toString()
  const url = queryString ? `${endpoint}?${queryString}` : endpoint
  return request(url, { method: 'GET' })
}

async function post(endpoint: string, data: unknown) {
  return request(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

async function put(endpoint: string, data: unknown) {
  return request(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

async function del(endpoint: string) {
  return request(endpoint, { method: 'DELETE' })
}

export const api = {
  validateCode: (code: string): Promise<ValidateResponse> => post('/auth/validate', { code }),

  getDevices: (project: string): Promise<Device[]> => get('/devices', { project }),

  getGraphData: async (project: string): Promise<GraphDataResponse> => {
    const response = await fetch(`${API_BASE_URL}/data/graph`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(project),
    })
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
    return response.json()
  },

  getReportData: (project: string, date: string, type: string, endDate?: string | null): Promise<ReportData> => {
    const body: Record<string, unknown> = { project, date, type: parseInt(type) }
    if (endDate) body.end_date = endDate
    return post('/reports/view', body)
  },

  downloadReport: async (project: string, date: string, type: string, endDate?: string | null) => {
    const body: Record<string, unknown> = { project, date, type: parseInt(type) }
    if (endDate) body.end_date = endDate

    const response = await fetch(`${API_BASE_URL}/reports/download`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

    const contentDisposition = response.headers.get('Content-Disposition')
    let filename = `TT_Report_${project}_${type === '1' ? 'Daily' : type === '2' ? 'Weekly' : 'Custom'}_${date.replace(/-/g, '')}.xlsx`

    if (contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (match?.[1]) filename = match[1].replace(/['"]/g, '')
    }

    const blob = await response.blob()
    const downloadUrl = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = downloadUrl
    a.download = filename
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(downloadUrl)
    document.body.removeChild(a)
  },

  getProjects: () => get('/projects'),

  // --- Admin ---

  adminListProjects: (): Promise<{ project: string; device_count: number; link_count: number }[]> =>
    get('/admin/projects'),

  // --- Admin CRUD ---

  adminListDevices: (params: Record<string, string> = {}): Promise<PaginatedResponse<AdminDevice>> =>
    get('/admin/devices', params),
  adminGetDevice: (id: number): Promise<AdminDevice> => get(`/admin/devices/${id}`),
  adminCreateDevice: (data: Partial<AdminDevice>): Promise<AdminDevice> => post('/admin/devices', data),
  adminUpdateDevice: (id: number, data: Partial<AdminDevice>): Promise<AdminDevice> => put(`/admin/devices/${id}`, data),
  adminDeleteDevice: (id: number) => del(`/admin/devices/${id}`),

  adminListLinks: (params: Record<string, string> = {}): Promise<PaginatedResponse<AdminLink>> =>
    get('/admin/links', params),
  adminGetLink: (id: number): Promise<AdminLink> => get(`/admin/links/${id}`),
  adminCreateLink: (data: Partial<AdminLink>): Promise<AdminLink> => post('/admin/links', data),
  adminUpdateLink: (id: number, data: Partial<AdminLink>): Promise<AdminLink> => put(`/admin/links/${id}`, data),
  adminDeleteLink: (id: number) => del(`/admin/links/${id}`),

  adminListTravelTimes: (params: Record<string, string> = {}): Promise<PaginatedResponse<AdminTravelTime>> =>
    get('/admin/travel-times', params),
  adminListSightings: (params: Record<string, string> = {}): Promise<PaginatedResponse<AdminSighting>> =>
    get('/admin/sightings', params),
  adminListBaseTT: (params: Record<string, string> = {}): Promise<PaginatedResponse<AdminBaseTT>> =>
    get('/admin/base-tt', params),
  adminListBaseData: (params: Record<string, string> = {}): Promise<PaginatedResponse<AdminBaseData>> =>
    get('/admin/base-data', params),
}

export function parseDataString(dataString: string, linksMeta: TravelTimeLinkMeta[] = []): TravelTimeLink[] {
  const parts = dataString.split(',')
  let index = 1
  const count = parseInt(parts[index++])
  const data: TravelTimeLink[] = []

  for (let i = 0; i < count; i++) {
    data.push({
      linkName: parts[index++],
      subtitle: linksMeta[i]?.subtitle ?? undefined,
      baseTT: parseInt(parts[index++]) || 0,
      baseSpeed: parseInt(parts[index++]) || 0,
      delay: parseInt(parts[index++]) || 0,
      currentTT: parseInt(parts[index++]) || 0,
      currentSpeed: parseInt(parts[index++]) || 0,
      last90MinTT: parts[index++] || '',
      last90MinBase: parts[index++] || '',
    })
  }

  return data
}

export function parseGraphData(dataString: string): (number | null)[] {
  if (!dataString || dataString.trim() === '') return []
  return dataString
    .trim()
    .split(/\s+/)
    .map((x) => {
      const val = parseInt(x)
      return isNaN(val) || val === 1 ? null : val
    })
}
